/* AtomiQ theme v4: shared behaviour (ES module, no dependencies).
   Cart over Ajax (forms with data-aq-add, the drawer section re-rendered from every answer), add-to-cart feedback
   (pending, check, count bump, spoken and shown confirmation), native dialogs (cart drawer, phone menu, order
   sheet), the site-wide motion switch, reveal fallback, grounds under fixed bars, the shared scroll scheduler
   (AQ.onScroll), pictures that wait for their popover, swipe rails and accordions.
   Every contract is documented in docs/v4/design-system.md, section 10. */

const AQ = (window.AQ = window.AQ || {});
const root = document.documentElement;
/* The layout falls back to the no-JS state when this file is late; undo that on arrival. */
root.classList.replace('no-js', 'js');

const $ = (selector, scope = document) => scope.querySelector(selector);
const $$ = (selector, scope = document) => [...scope.querySelectorAll(selector)];
const reducedQuery = matchMedia('(prefers-reduced-motion: reduce)');

AQ.emit = (name, detail = {}) => document.dispatchEvent(new CustomEvent(name, { detail }));
AQ.on = (name, fn) => document.addEventListener(name, fn);

/* Scroll anchoring off for a moment: a section that scrolls the page itself while content above the anchor grows
   (a step change that resizes an instrument, a panel that opens) calls AQ.holdAnchor(ms), so the browser does not
   add the growth on top of the scripted scroll. Repeated calls extend the hold. */
let anchorHold = 0;
AQ.holdAnchor = (ms = 1000) => {
  document.body.style.overflowAnchor = 'none';
  clearTimeout(anchorHold);
  anchorHold = setTimeout(() => document.body.style.removeProperty('overflow-anchor'), ms);
};

/* One scroll scheduler for the whole page (round 3): one passive listener and one requestAnimationFrame per frame,
   every job's read (layout: rects, scrollY) before any job's write (styles), so no job forces a style flush on
   another. AQ.onScroll(read, write) returns a function that removes the job. read(y) gets scrollY and returns
   anything; write(value, y) gets it back. Use an IntersectionObserver to add a job only while its section is near
   the screen, and remove it when the section is far away. */
const scrollJobs = new Set();
let scrollFrame = 0;
const runScrollJobs = () => {
  scrollFrame = 0;
  const y = scrollY;
  const jobs = [...scrollJobs];
  const values = jobs.map((job) => {
    try {
      return job.read ? job.read(y) : undefined;
    } catch {
      return undefined;
    }
  });
  jobs.forEach((job, i) => {
    try {
      job.write?.(values[i], y);
    } catch {
      /* one broken job never stops the others */
    }
  });
};
const queueScrollJobs = () => {
  if (!scrollFrame) scrollFrame = requestAnimationFrame(runScrollJobs);
};
AQ.onScroll = (read, write) => {
  const job = { read, write };
  if (!scrollJobs.size) {
    addEventListener('scroll', queueScrollJobs, { passive: true });
    addEventListener('resize', queueScrollJobs, { passive: true });
  }
  scrollJobs.add(job);
  queueScrollJobs();
  return () => {
    scrollJobs.delete(job);
    if (!scrollJobs.size) {
      removeEventListener('scroll', queueScrollJobs);
      removeEventListener('resize', queueScrollJobs);
    }
  };
};

/* A string from the layout with [name] placeholders filled in. */
AQ.t = (key, vars = {}) => {
  let text = AQ.strings?.[key] || '';
  for (const [name, value] of Object.entries(vars)) text = text.split(`[${name}]`).join(String(value));
  return text;
};

AQ.money = (cents) => {
  const format = AQ.moneyFormat || '€{{amount_with_comma_separator}}';
  const value = (Number(cents) || 0) / 100;
  const fmt = (digits, thousands, decimal) => {
    const [int, frac] = value.toFixed(digits).split('.');
    const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return frac ? grouped + decimal + frac : grouped;
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return fmt(0, ',', '.');
      case 'amount_with_comma_separator':
        return fmt(2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return fmt(0, '.', ',');
      case 'amount_with_apostrophe_separator':
        return fmt(2, "'", '.');
      default:
        return fmt(2, ',', '.');
    }
  });
};

/* ------------------------------------------------------------ spoken and shown confirmations */

/* The page-wide live region (layout). Inside an open modal dialog nothing outside it is read out, so the cart
   writes into the drawer's own status line instead. */
AQ.announce = (text, region = $('[data-aq-status]')) => {
  if (!region || !text) return;
  region.textContent = '';
  requestAnimationFrame(() => {
    region.textContent = text;
  });
};

let toastTimer = 0;
AQ.toast = (text) => {
  const toast = $('[data-aq-toast]');
  if (!toast || !text) return;
  const label = $('[data-aq-toast-text]', toast);
  if (label) label.textContent = text;
  const header = $('.site-header');
  const bottom = header ? Math.max(header.getBoundingClientRect().bottom, 0) : 0;
  toast.style.top = `${Math.round(bottom + 12)}px`;
  toast.classList.add('is-on');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => toast.classList.remove('is-on'), 2800);
};

/* ------------------------------------------------------------ dialogs */

/* Native <dialog> with showModal(): focus moves in, Esc closes (the browser), a click on the backdrop closes,
   focus goes back to the opener, the page stops scrolling while one is open. */
const openers = new WeakMap();

const anyOpen = () => Boolean($('dialog[open]'));

function settle(dialog, restore) {
  const opener = openers.get(dialog);
  openers.delete(dialog);
  if (opener?.hasAttribute?.('aria-expanded')) opener.setAttribute('aria-expanded', 'false');
  if (!anyOpen()) root.classList.remove('has-dialog');
  if (restore && opener?.isConnected && (document.activeElement === document.body || dialog.contains(document.activeElement) || !document.activeElement)) {
    opener.focus({ preventScroll: true });
  }
  AQ.emit('aq:dialog', { open: false, id: dialog.id, dialog });
}

AQ.dialog = {
  get current() {
    return $$('dialog[open]').pop() || null;
  },
  open(dialog, opener = document.activeElement) {
    if (!dialog) return;
    if (dialog.open) return;
    $$('dialog[open]').forEach((other) => {
      if (other !== dialog) AQ.dialog.close(other, { restore: false });
    });
    if (opener && opener !== document.body) openers.set(dialog, opener);
    if (opener?.hasAttribute?.('aria-controls')) opener.setAttribute('aria-expanded', 'true');
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    root.classList.add('has-dialog');
    AQ.emit('aq:dialog', { open: true, id: dialog.id, dialog });
  },
  close(dialog = AQ.dialog.current, { restore = true } = {}) {
    if (!dialog?.open) return;
    /* Our own close: the close event (sync or async) has nothing left to do. Settle now, so a caller can
       scroll the page in the same turn. */
    dialog.aqHandled = true;
    if (typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
    settle(dialog, restore);
  },
};

/* Esc and form[method=dialog] close the dialog natively: settle on the close event (it does not bubble). */
document.addEventListener(
  'close',
  (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement)) return;
    if (dialog.aqHandled) {
      dialog.aqHandled = false;
      return;
    }
    settle(dialog, true);
  },
  true
);

const outside = (dialog, event) => {
  const r = dialog.getBoundingClientRect();
  return event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom;
};

/* ------------------------------------------------------------ motion switch (WCAG 2.2.2) */

const motion = {
  paused: root.classList.contains('is-motion-paused'),
  set(on, remember = true) {
    motion.paused = Boolean(on);
    root.classList.toggle('is-motion-paused', motion.paused);
    root.classList.toggle('is-paused', motion.paused);
    if (remember) {
      try {
        localStorage.setItem('aq-motion-paused', motion.paused ? '1' : '0');
      } catch {
        /* private mode: the choice holds for this page only */
      }
    }
    syncMotionButtons();
    $$('.aq-3d, aq-explainer').forEach(pauseScene);
    AQ.emit('aq:motion', { paused: motion.paused });
  },
};
AQ.motionSwitch = motion;

function pauseScene(scene) {
  try {
    if (typeof scene.pause !== 'function') {
      /* Not upgraded yet: apply the state once its definition arrives (aq:ready repeats it). */
      const tag = scene.localName;
      if (tag.includes('-') && !customElements.get(tag)) customElements.whenDefined(tag).then(() => pauseScene(scene));
      return;
    }
    if (motion.paused) scene.pause(true);
    else if (typeof scene.resume === 'function') scene.resume();
    else scene.pause(false);
  } catch {
    /* a scene that cannot be paused simply keeps running */
  }
}

/* A switch with a fixed name says its state with aria-pressed; one whose visible label swaps
   (data-aq-label-swap) already says it in words. */
function syncMotionButtons() {
  $$('[data-aq-motion-toggle]').forEach((button) => {
    if (button.hasAttribute('data-aq-label-swap')) button.removeAttribute('aria-pressed');
    else button.setAttribute('aria-pressed', String(motion.paused));
    button.classList.toggle('is-paused', motion.paused);
  });
}

/* A 3D scene that becomes ready after the visitor paused the site starts paused. */
AQ.on('aq:ready', (event) => {
  if (motion.paused) pauseScene(event.target);
});

/* ------------------------------------------------------------ reveal fallback
   Browsers with scroll-driven animations reveal in CSS; the rest get a class once the element is in view. */

const cssReveal = window.CSS?.supports?.('animation-timeline: view()');
let revealer = null;

function reveal(scope = document) {
  const items = $$('[data-reveal]:not(.is-in)', scope);
  if (!items.length || cssReveal) return;
  if (!('IntersectionObserver' in window) || reducedQuery.matches || motion.paused) {
    items.forEach((el) => el.classList.add('is-in'));
    return;
  }
  revealer ||= new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        revealer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
  );
  items.forEach((el) => revealer.observe(el));
}

/* ------------------------------------------------------------ grounds under fixed bars */

/* The ground (graphite or bright) under a horizontal line of the viewport, from the sections that carry
   data-ground (or the v3 data-field). One IntersectionObserver per follower; the line moves with resize. */
const GROUNDS = '[data-ground], [data-field]';
const groundOf = (el) => {
  const value = el.dataset.ground || el.dataset.field;
  return value === 'bright' || value === 'alu' ? 'bright' : 'graphite';
};

AQ.followGround = (el, lineY) => {
  if (!el || !('IntersectionObserver' in window)) return () => {};
  let io = null;
  const under = new Set();
  const apply = () => {
    let pick = null;
    for (const section of under) if (!pick || pick.compareDocumentPosition(section) & Node.DOCUMENT_POSITION_FOLLOWING) pick = section;
    const next = pick ? groundOf(pick) : 'graphite';
    if (el.dataset.on !== next) el.dataset.on = next;
  };
  const watch = () => {
    io?.disconnect();
    under.clear();
    const y = Math.round(Math.min(Math.max(lineY(), 1), innerHeight - 2));
    io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => (entry.isIntersecting ? under.add(entry.target) : under.delete(entry.target)));
        apply();
      },
      { rootMargin: `-${y}px 0px -${innerHeight - y - 1}px 0px` }
    );
    $$(GROUNDS).forEach((section) => {
      if (!el.contains(section) && !section.contains(el)) io.observe(section);
    });
  };
  watch();
  let timer = 0;
  const later = () => {
    clearTimeout(timer);
    timer = setTimeout(watch, 150);
  };
  addEventListener('resize', later, { passive: true });
  document.addEventListener('shopify:section:load', later);
  return () => {
    io?.disconnect();
    removeEventListener('resize', later);
    document.removeEventListener('shopify:section:load', later);
  };
};

/* Fixed or sticky bars that ask for it (the phone buy bar) follow the ground under their own middle. */
function followBars(scope = document) {
  $$('[data-ground-follow]', scope).forEach((bar) => {
    if (bar.aqFollow) return;
    bar.aqFollow = AQ.followGround(bar, () => {
      const style = getComputedStyle(bar);
      const half = bar.offsetHeight / 2;
      return style.top !== 'auto' && style.position === 'fixed'
        ? parseFloat(style.top) + half
        : innerHeight - (parseFloat(style.bottom) || 0) - half;
    });
  });
}

/* ------------------------------------------------------------ cart */

/* The drawer is the section "cart-drawer"; every cart call asks Shopify to render it again. */
const SECTION = 'cart-drawer';

const countEls = () => $$('[data-aq-cart-count]');

function setCount(count, previous) {
  countEls().forEach((el) => {
    el.textContent = String(count);
    el.dataset.count = String(count);
    el.hidden = count === 0;
    if (count > previous) {
      el.classList.remove('is-bump');
      void el.offsetWidth;
      el.classList.add('is-bump');
    }
  });
  $$('[data-aq-cart-count-text]').forEach((el) => {
    el.textContent = String(count);
  });
  const label = AQ.t(count === 1 ? 'cartLabelOne' : 'cartLabelOther', { count });
  if (label) $$('[data-aq-cart-label]').forEach((el) => el.setAttribute('aria-label', label));
  AQ.cart.count = count;
}

function applyCartSection(html) {
  if (!html) return;
  const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-aq-cart]');
  const current = $('[data-aq-cart]');
  const previous = AQ.cart.count ?? Number(current?.dataset.count || 0);
  if (next && current) {
    /* Keep the keyboard user's place: the panel is replaced, so focus is restored by key. */
    const active = document.activeElement;
    const key = current.contains(active) ? active.dataset.focus || 'close' : null;
    current.replaceWith(next);
    if (key) {
      const target = next.querySelector(`[data-focus="${CSS.escape(key)}"]`) || next.querySelector('[data-aq-cart-close]');
      target?.focus({ preventScroll: true });
    }
    reveal(next);
  }
  const count = Number((next || current)?.dataset.count || 0);
  setCount(count, previous);
  AQ.emit('aq:cart:updated', { count, previous });
}

async function cartRequest(url, body) {
  const isForm = body instanceof FormData;
  if (isForm) {
    body.append('sections', SECTION);
    body.append('sections_url', window.location.pathname);
  } else {
    body = JSON.stringify({ ...body, sections: SECTION, sections_url: window.location.pathname });
  }
  let res;
  let data;
  try {
    res = await fetch(`${url}.js`, {
      method: 'POST',
      headers: isForm
        ? { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
        : { Accept: 'application/json', 'Content-Type': 'application/json' },
      body,
    });
    data = await res.json();
  } catch {
    /* Network failure or a non-JSON answer (rate limit, password page). */
    throw new Error(AQ.strings?.cartError || 'Cart error');
  }
  if (!res.ok || data.status) {
    throw new Error(data.description || data.message || AQ.strings?.cartError || 'Cart error');
  }
  return data;
}

/* What to say after an add: one product by name (the two formulas by their short names from the copy), the duo,
   or a count. */
/* The plain name as on the can: a leading shop or vendor word is dropped ("AtomiQ Fuel System Restore" becomes
   "Fuel System Restore"), the same rule as snippets/cart-name.liquid. */
const shortName = (title, vendor) => {
  const words = String(title || '').trim().split(/\s+/);
  const first = (words[0] || '').toLowerCase();
  const brand = [AQ.shop, vendor].filter(Boolean).map((w) => String(w).toLowerCase());
  return words.length > 2 && brand.includes(first) ? words.slice(1).join(' ') : words.join(' ');
};

const nameOf = (item) => {
  const names = new Map((AQ.names || []).filter(([id]) => id));
  return names.get(item.product_id) || shortName(item.product_title || item.title, item.vendor);
};

function addedMessage(data, form) {
  if (form?.dataset.aqAdded) return form.dataset.aqAdded;
  const items = Array.isArray(data.items) ? data.items : [data];
  if (items.length === 1) return AQ.t('added', { name: nameOf(items[0]) });
  if (items.length === 2) return AQ.t('addedDuo');
  const count = items.reduce((sum, item) => sum + (Number(item.quantity) || 1), 0);
  return AQ.t('addedMany', { count });
}

AQ.cart = {
  count: undefined,
  dialog: () => $('[data-aq-cart-dialog]'),
  async add(formOrItems, { quiet = false, form = null } = {}) {
    const body = formOrItems instanceof FormData ? formOrItems : { items: formOrItems };
    const data = await cartRequest(AQ.routes.cartAdd, body);
    applyCartSection(data.sections?.[SECTION]);
    const message = addedMessage(data, form);
    if (quiet || !AQ.cart.dialog()) {
      AQ.toast(message);
      AQ.announce(message);
    } else {
      AQ.cart.open();
      /* Say it inside the dialog once it is open, so screen readers hear what happened. */
      requestAnimationFrame(() => {
        const status = $('[data-aq-cart-status]');
        const summary = $('[data-aq-cart]')?.dataset.summary;
        AQ.announce([message, summary].filter(Boolean).join(' '), status || undefined);
      });
    }
    AQ.emit('aq:cart:added', { items: Array.isArray(data.items) ? data.items : [data], quiet });
    return data;
  },
  async change(line, quantity) {
    const data = await cartRequest(AQ.routes.cartChange, { line, quantity });
    applyCartSection(data.sections?.[SECTION]);
    return data;
  },
  async refresh() {
    const res = await fetch(`${AQ.routes.root.replace(/\/$/, '')}/?sections=${SECTION}`);
    const data = await res.json();
    applyCartSection(data[SECTION]);
  },
  open(opener = document.activeElement) {
    const dialog = AQ.cart.dialog();
    if (dialog && !dialog.open) AQ.dialog.open(dialog, opener);
  },
  close() {
    const dialog = AQ.cart.dialog();
    if (dialog?.open) AQ.dialog.close(dialog);
  },
};

/* Add-to-cart feedback on the submit button: busy (spinner), then done (check) for 1.6 s. */
function busy(button, on) {
  if (!button) return;
  if (on) {
    button.setAttribute('aria-busy', 'true');
    button.dataset.state = 'busy';
  } else {
    button.removeAttribute('aria-busy');
    if (button.dataset.state === 'busy') delete button.dataset.state;
  }
}

function done(button) {
  if (!button) return;
  button.dataset.state = 'done';
  clearTimeout(button.aqDone);
  button.aqDone = setTimeout(() => {
    if (button.dataset.state === 'done') delete button.dataset.state;
  }, 1600);
}

document.addEventListener('submit', async (event) => {
  const form = event.target.closest('form[data-aq-add]');
  if (!form) return;
  event.preventDefault();
  const button = event.submitter?.matches?.('[type="submit"], button:not([type])') ? event.submitter : form.querySelector('[type="submit"], button:not([type])');
  if (button?.getAttribute('aria-busy') === 'true') return;
  const error = $('[data-aq-error]', form);
  if (error) error.hidden = true;
  busy(button, true);
  const quiet = form.dataset.aqAdd === 'quiet';
  try {
    await AQ.cart.add(new FormData(form), { quiet, form });
    busy(button, false);
    done(button);
  } catch (err) {
    busy(button, false);
    if (error) {
      error.textContent = err.message;
      error.hidden = false;
    } else {
      window.location.href = AQ.routes.cart;
    }
  }
});

/* ------------------------------------------------------------ clicks */

document.addEventListener('click', async (event) => {
  const target = event.target;

  const cartOpener = target.closest('[data-aq-cart-open]');
  if (cartOpener && AQ.cart.dialog()) {
    event.preventDefault();
    AQ.cart.open(cartOpener);
    return;
  }

  if (target.closest('[data-aq-cart-close]')) {
    event.preventDefault();
    AQ.cart.close();
    return;
  }

  const opener = target.closest('[data-aq-dialog-open]');
  if (opener) {
    const dialog = document.getElementById(opener.dataset.aqDialogOpen);
    if (dialog) {
      event.preventDefault();
      AQ.dialog.open(dialog, opener);
      return;
    }
  }

  const closer = target.closest('[data-aq-dialog-close]');
  if (closer) {
    event.preventDefault();
    AQ.dialog.close(closer.closest('dialog'));
    return;
  }

  /* A click on the backdrop of a modal dialog (the target is the dialog itself, outside its box). */
  if (target instanceof HTMLDialogElement && target.open && outside(target, event)) {
    AQ.dialog.close(target);
    return;
  }

  /* A link inside a dialog closes it first, so the page can scroll to an anchor behind it. */
  const link = target.closest('dialog[open] a[href]');
  if (link && !link.hasAttribute('data-aq-keep-open') && !event.defaultPrevented) {
    AQ.dialog.close(link.closest('dialog'), { restore: false });
  }

  const change = target.closest('[data-aq-change]');
  if (change) {
    event.preventDefault();
    const panel = change.closest('[data-aq-cart]');
    panel?.setAttribute('aria-busy', 'true');
    try {
      await AQ.cart.change(Number(change.dataset.line), Number(change.dataset.qty));
    } catch {
      window.location.href = AQ.routes.cart;
    }
    return;
  }

  const motionToggle = target.closest('[data-aq-motion-toggle]');
  if (motionToggle) {
    motion.set(!motion.paused);
    return;
  }

  const qty = target.closest('[data-aq-qty]');
  if (qty) {
    const input = qty.closest('.qty, .aq-qty')?.querySelector('input');
    if (!input) return;
    const min = Number(input.min || 1);
    const max = input.max ? Number(input.max) : Infinity;
    const next = Math.min(max, Math.max(min, (Number(input.value) || min) + Number(qty.dataset.aqQty)));
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }
});

/* The order note in the drawer is saved as it changes, because the drawer is re-rendered on every cart update. */
document.addEventListener('change', (event) => {
  const note = event.target.closest?.('[data-aq-note]');
  if (!note) return;
  fetch(`${AQ.routes.cartUpdate}.js`, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ note: note.value }),
  }).catch(() => {});
});

/* ------------------------------------------------------------ pictures that wait for their popover or dialog
   An <img data-src> (snippets/asset-image.liquid with defer) gets its sources when the popover or dialog around it
   opens, and already when a finger or the mouse goes down on the button that opens it, so the photo is usually
   there by the time the popover shows. A closed popover never downloads anything. */

function wake(scope) {
  if (!scope?.querySelectorAll) return;
  scope.querySelectorAll('img[data-src]').forEach((img) => {
    if (img.dataset.srcset) img.srcset = img.dataset.srcset;
    img.src = img.dataset.src;
    img.removeAttribute('data-src');
    img.removeAttribute('data-srcset');
  });
}

document.addEventListener(
  'beforetoggle',
  (event) => {
    if (event.newState === 'open') wake(event.target);
  },
  true
);

document.addEventListener(
  'pointerdown',
  (event) => {
    const opener = event.target.closest?.('[popovertarget], [data-aq-dialog-open]');
    if (!opener) return;
    const id = opener.getAttribute('popovertarget') || opener.dataset.aqDialogOpen;
    wake(id ? document.getElementById(id) : null);
  },
  { capture: true, passive: true }
);

/* Dialogs opened by script (showModal) do not send beforetoggle in every browser. */
AQ.on('aq:dialog', (event) => {
  if (event.detail?.open) wake(event.detail.dialog);
});

/* ------------------------------------------------------------ swipe rails and accordions */

/* A swipe rail is reachable by keyboard only while it actually scrolls. */
function rails(scope = document) {
  $$('[data-rail]', scope).forEach((rail) => {
    if (rail.scrollWidth > rail.clientWidth + 4) {
      if (!rail.hasAttribute('tabindex')) rail.tabIndex = 0;
    } else if (rail.tabIndex === 0) rail.removeAttribute('tabindex');
  });
}

/* One answer open at a time (native name="" does this in new browsers; this covers the rest). */
document.addEventListener(
  'toggle',
  (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;
    const group = details.closest('[data-accordion="single"], [data-faq]');
    if (!group) return;
    $$('details[open]', group).forEach((other) => {
      if (other !== details && other.closest('[data-accordion="single"], [data-faq]') === group) other.open = false;
    });
  },
  true
);

/* Theme editor: a selected block inside an accordion opens so it can be edited in view. */
document.addEventListener('shopify:block:select', (event) => {
  const details = event.target.closest?.('details');
  if (details) details.open = true;
});

/* ------------------------------------------------------------ start */

function init(scope = document) {
  reveal(scope);
  followBars(scope);
  rails(scope);
}

syncMotionButtons();
if (motion.paused) $$('.aq-3d, aq-explainer').forEach(pauseScene);
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
else init();

AQ.cart.count = Number($('[data-aq-cart]')?.dataset.count ?? $('[data-aq-cart-count]')?.dataset.count ?? 0);

let railTimer = 0;
addEventListener(
  'resize',
  () => {
    clearTimeout(railTimer);
    railTimer = setTimeout(() => rails(), 200);
  },
  { passive: true }
);
document.addEventListener('shopify:section:load', (event) => init(event.target));
AQ.on('aq:cart:updated', () => reveal());

/* Coming back with the browser's Back button can show a stale cart: refresh it. */
window.addEventListener('pageshow', (event) => {
  if (event.persisted) AQ.cart.refresh().catch(() => {});
});
